import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as countryService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import FilePreview from "../../FilePreview/FilePreview";
import { useLoadScript } from "@react-google-maps/api";
import ImageInput from "../../common/ImageInput";

import { GOOGLE_MAPS_API_KEY } from "../../../config/googleMaps";
import PlaceSearchInput from "../../common/PlaceSearchInput";
import ToggleButton from "../../common/ToggleButton";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  DragHandleButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";

const libraries = ["places"];

const CountryThumb = ({ imageUrl }) => {
  const [hasError, setHasError] = React.useState(false);
  const hasImage = Boolean(imageUrl) && !hasError;

  if (!hasImage) {
    return (
      <div
        className="flex h-7 w-14 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-400"
        title="No image"
      >
        No image
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt="Country preview"
      className="h-7 w-7 object-cover rounded-md"
      onError={() => setHasError(true)}
    />
  );
};

const SortableRow = ({
  country,
  index,
  moveRow,
  handleEditClick,
  handleDeleteClick,
  handleDragEnd,
}) => {
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "row",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        handleDragEnd();
      }
    },
  });

  const [, drop] = useDrop({
    accept: "row",
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <tr
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={`hover:bg-gray-50 ${isDragging ? "bg-gray-100 shadow-lg" : ""}`}
    >
      <AdminTableCell>{index + 1}</AdminTableCell>
      <AdminTableCell>
        <CountryThumb imageUrl={country.image_url} />
      </AdminTableCell>
      <AdminTableCell>{country.name}</AdminTableCell>
      <AdminTableCell>
        <StatusBadge variant={getStatusVariant(country.status)}>
          {country.status === 1 ? "Active" : "In-Active"}
        </StatusBadge>
      </AdminTableCell>
      <AdminTableCell>
        <StatusBadge variant={country.display_web === 1 ? "active" : "inactive"}>
          {country.display_web === 1 ? "Shown" : "Hidden"}
        </StatusBadge>
      </AdminTableCell>
      <TableActionsCell>
        <EditActionButton
          showLabel={false}
          onClick={() => handleEditClick(country.id)}
        />
        <DeleteActionButton
          showLabel={false}
          onClick={() => handleDeleteClick(country)}
        />
        <DragHandleButton isDragging={isDragging} />
      </TableActionsCell>
    </tr>
  );
};

const Countries = () => {
  const queryClient = useQueryClient();
  const [orderedCountries, setOrderedCountries] = useState([]);

  // State management
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [countryName, setCountryName] = useState("");
  const [countryPlaceId, setCountryPlaceId] = useState("");
  const [countryStatus, setCountryStatus] = useState(1);
  const [displayWeb, setDisplayWeb] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [countryCode, setCountryCode] = useState("");
  const [countryImage, setCountryImage] = useState(null);

  const [countryLatitude, setCountryLatitude] = useState("");
  const [countryLongitude, setCountryLongitude] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    code: "",
    image: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: countryData } = useQuery({
    queryKey: ["countries", currentPage, activeSearch],
    queryFn: () =>
      countryService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Update orderedCountries when countries change
  useEffect(() => {
    if (countryData?.data?.data) {
      // Sort countries based on sort_order before setting state
      const sortedCountries = [...countryData.data.data].sort((a, b) => {
        // Handle null or undefined sort_order values
        const orderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
      setOrderedCountries(sortedCountries);
    }
  }, [countryData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: countryService.create,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Country added successfully!");
      setAddDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["countries"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to add country");
      setShowError(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: countryService.update,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Country updated successfully!");
      setEditDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["countries"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to update country");
      setShowError(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: countryService.deleteCountry,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Country deleted successfully!");
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["countries"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to delete country");
      setShowError(true);
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: countryService.getById,
    onSuccess: (data) => {
      setIsLoading(false);
      setSelectedCountry(data.data);
      setCountryName(data.data.name);
      setCountryStatus(data.data.status);
      setCountryCode(data.data.code || "");
      setCountryImage(null);
      setCountryLatitude(data.data.latitude || "");
      setCountryLongitude(data.data.longitude || "");
      setCountryPlaceId(data.data.place_id || "");
      setDisplayWeb(data.data.display_web === 1);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.message || "Failed to fetch country details");
    },
  });

  // Add sort mutation
  const sortMutation = useMutation({
    mutationFn: countryService.sort,
    onSuccess: (success) => {
      showSuccessMessage(success.response?.data?.message || "Countries reordered successfully!");
      queryClient.invalidateQueries(["countries"]);
    },
    onError: (error) => {
      setErrorMessage(error.response?.data?.message || "Failed to reorder countries");
      setShowError(true);
    },
  });

  // Helper functions
  const resetForm = () => {
    setCountryName("");
    setCountryCode("");
    setCountryImage(null);
    setCountryLatitude("");
    setCountryLongitude("");
    setErrors({ name: "", code: "", image: "" });
  };

  const validateForm = () => {
    let tempErrors = {
      name: "",
      code: "",
      image: "",
    };
    let isValid = true;

    if (!countryName.trim()) {
      tempErrors.name = "Country name is required";
      isValid = false;
    }

    if (!countryCode.trim()) {
      tempErrors.code = "Country code is required";
      isValid = false;
    }

    if (!countryImage && !selectedCountry) {
      tempErrors.image = "Country image is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setErrorMessage(errorMsg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  const handlePlaceSelect = (placeDetails) => {
    console.log(placeDetails);
    if (placeDetails) {
      setCountryName(placeDetails.address_components[0].long_name);
      setCountryLatitude(placeDetails.lat);
      setCountryLongitude(placeDetails.lng);
      setCountryPlaceId(placeDetails.place_id);
      setCountryCode(placeDetails.address_components[0].short_name);
      setErrors({ ...errors, name: "" });
    } else {
      setCountryName("");
      setCountryLatitude("");
      setCountryLongitude("");
      setCountryPlaceId("");
      setCountryCode("");
    }
  };

  // Action handlers
  const handleAddClick = () => {
    resetForm();
    setCountryStatus(1);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (country) => {
    setSelectedCountry(country);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (countryId) => {
    getByIdMutation.mutate(countryId);
  };

  const handleSaveCountry = () => {
    setIsLoading(true);
    if (validateForm()) {
    const formData = new FormData();
    formData.append("name", countryName);
    formData.append("code", countryCode);
    formData.append("image", countryImage);
    formData.append("status", countryStatus);
    formData.append("place_id", countryPlaceId);
    formData.append("latitude", countryLatitude);
    formData.append("longitude", countryLongitude);
    formData.append("display_web", displayWeb ? 1 : 0);
    createMutation.mutate(formData);
    }else{
      setIsLoading(false);
    }
  };

  const handleUpdateCountry = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", countryName);
      formData.append("code", countryCode);
      formData.append("status", countryStatus);
      formData.append("place_id", countryPlaceId);
      formData.append("latitude", countryLatitude);
      formData.append("longitude", countryLongitude);
      formData.append("display_web", displayWeb ? 1 : 0);

      if (countryImage) {
        formData.append("image", countryImage);
      }

      updateMutation.mutate({
        id: selectedCountry.id,
        countryData: formData,
      });
    }else{
      setIsLoading(false);
    }
  };

  const handleDeleteCountry = () => {
    setIsLoading(true);
    if (selectedCountry) {
      deleteMutation.mutate(selectedCountry.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };



  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const moveRow = (dragIndex, hoverIndex) => {
    setDragIndex(dragIndex);
    setHoverIndex(hoverIndex);
    const newOrderedCountries = [...orderedCountries];
    const [removed] = newOrderedCountries.splice(dragIndex, 1);
    newOrderedCountries.splice(hoverIndex, 0, removed);
    setOrderedCountries(newOrderedCountries);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null && hoverIndex !== null) {
      const sort_from = orderedCountries[dragIndex]?.id;
      const sort_to = orderedCountries[hoverIndex]?.id;
      if (sort_from && sort_to) {
        sortMutation.mutate({ sort_from, sort_to });
      }
    }
    setDragIndex(null);
    setHoverIndex(null);
  };

  // Error handling for Google Maps
  if (loadError) {
    return (
      <AdminPageLayout>
        <AdminPageHeader
          title={ADMIN_PAGE_META.countries.title}
          subtitle={ADMIN_PAGE_META.countries.subtitle}
        />
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Google Maps API is not properly configured. Please check your API key.
              </p>
            </div>
          </div>
        </div>
      </AdminPageLayout>
    );
  }

  if (!isLoaded) {
    return (
      <AdminPageLayout>
        <AdminPageHeader
          title={ADMIN_PAGE_META.countries.title}
          subtitle={ADMIN_PAGE_META.countries.subtitle}
        />
        <div className="p-6 text-gray-500">Loading...</div>
      </AdminPageLayout>
    );
  }


  const pagination = {
    currentPage: countryData?.data?.current_page || 1,
    lastPage: countryData?.data?.last_page || 1,
    perPage: countryData?.data?.per_page || 10,
    total: countryData?.data?.total || 0,
    from: countryData?.data?.from || 0,
    to: countryData?.data?.to || 0,
  };

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.countries.title}
        subtitle={ADMIN_PAGE_META.countries.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              placeholder="Search countries..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add Country
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <DndProvider backend={HTML5Backend}>
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  showInfo={true}
                />
              </AdminTableFooter>
            }
          >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Preview</AdminTableTh>
            <AdminTableTh>Country Name</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh>Display</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {orderedCountries.length > 0 ? (
              orderedCountries.map((country, index) => (
                <SortableRow
                  key={country.id}
                  country={country}
                  index={index}
                  moveRow={moveRow}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  handleDragEnd={handleDragEnd}
                />
              ))
            ) : (
              <TableEmptyRow
                colSpan={6}
                emptyMessage="No countries found"
                searchQuery={activeSearch}
                onClearSearch={clearSearch}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </DndProvider>
      </AdminPageCard>

      {/* Add Country Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setErrors({ name: "", code: "", image: "" });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Country
          </div>
        }
        primaryButtonText="Add Country"
        primaryButtonAction={handleSaveCountry}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setErrors({ name: "", code: "", image: "" });
        }}
        isLoading={isLoading}
      >
        <div className="mb-4">
          <div className="flex flex-row gap-4">
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country Name <span className="text-red-500">*</span>
              </label>
              <PlaceSearchInput
                value={countryName}
                onChange={handlePlaceSelect}
                placeholder="Search for a country..."
                hasError={!!errors.name}
                types={["country"]}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
            <div className="w-1/3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country Code <span className="text-red-500">*</span>
              </label>
              <input disabled={true}
                type="text"
                className={`text-sm w-full px-3 py-1 border ${errors.code ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
                placeholder="Code"
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  setErrors({ ...errors, code: "" });
                }}
              />
              {errors.code && (
                <p className="text-red-500 text-xs mt-1">{errors.code}</p>
              )}
            </div>

          </div>
        </div>

        <div className="mb-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            className="text-sm w-full px-3 grid-cols-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
            value={countryStatus}
            onChange={(e) => setCountryStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={0}>In-Active</option>
          </select>
        </div>

        <div className="mb-4">
          <ImageInput
            onChange={(image) => {
              setCountryImage(image);
              setErrors({ ...errors, image: "" });
            }}
            label="Country Image"
            required={true}
            error={errors.image}
          />
          {errors.image && (
            <p className="text-red-500 text-xs mt-1">{errors.image}</p>
          )}
        </div>

        <div className="mb-4">
          <ToggleButton
            enabled={displayWeb}
            onChange={setDisplayWeb}
            label="Show country on website?"
          />
        </div>

        <label className="block font-bold text-sm text-gray-700 mb-1">
          Attachment preview
        </label>
        <div className="min-w-0 max-w-full">
        {countryImage && (
          <FilePreview
            file={countryImage}
            label={countryImage.name}
            onDelete={() => setCountryImage(null)}
            isImage={true}
            showDelete={false}
          />
        )}
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Country
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateCountry}
        closeButtonText="Cancel"
        closeButtonAction={() => setEditDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mb-4">
          <div className="flex flex-row gap-4">
            <div className="w-1/3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={`text-sm w-full px-3 py-1 border ${errors.code ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
                placeholder="Code"
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  setErrors({ ...errors, code: "" });
                }}
              />
              {errors.code && (
                <p className="text-red-500 text-xs mt-1">{errors.code}</p>
              )}
            </div>
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country Name <span className="text-red-500">*</span>
              </label>
              <PlaceSearchInput
                value={countryName}
                onChange={handlePlaceSelect}
                placeholder="Search for a country..."
                hasError={!!errors.name}
                types={["country"]}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            className="text-sm w-full px-3 grid-cols-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
            value={countryStatus}
            onChange={(e) => setCountryStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={0}>In-Active</option>
          </select>
        </div>

        <div className="mb-4">
          <ImageInput
            onChange={(image) => {
              setCountryImage(image);
              setErrors({ ...errors, image: "" });
            }}
            label="Country Image"
            error={errors.image}
          />
          {errors.image && (
            <p className="text-red-500 text-xs mt-1">{errors.image}</p>
          )}
        </div>

        <div className="mb-4">
          <ToggleButton
            enabled={displayWeb}
            onChange={setDisplayWeb}
            label="Show country on website?"
          />
        </div>

        {/* Image Preview Section */}
        <div className="min-w-0 max-w-full">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Attachment preview
          </label>
          {countryImage ? (
            <FilePreview
              file={countryImage}
              label={countryImage.name}
              onDelete={() => setCountryImage(null)}
              isImage={true}
              showDelete={true}
            />
          ) : selectedCountry && selectedCountry.image_url ? (
            <FilePreview
              fileUrl={selectedCountry.image_url}
              label="Current Image"
              isImage={true}
              onDelete={() => setCountryImage(null)}
              showDelete={true}
            />
          ) : (
            <div className="text-sm text-gray-500">No image selected</div>
          )}
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Country
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteCountry}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this country? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default Countries;
